import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, Card, CardContent, CardActionArea, useTheme, useMediaQuery } from '@mui/material';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';
import { showToast } from '../../utils/toastUtils';
import { setUseCase, setUserProfile, setCurrentStep } from '../../redux/onboardingReducer';
import './OnboardingUseCase.css';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PollIcon from '@mui/icons-material/Poll';


const useCaseOptions = [
  { value: 'pre_sales', label: 'Pre-Sales', icon: <ShoppingCartIcon /> },
  { value: 'sales', label: 'Sales', icon: <AttachMoneyIcon /> },
  { value: 'onboarding', label: 'Onboarding', icon: <PersonAddIcon /> },
  { value: 'customer_support', label: 'Customer Support', icon: <SupportAgentIcon /> },
  { value: 'survey', label: 'Survey', icon: <PollIcon /> },
];

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://aitaler-backend-production.devops-565.workers.dev').replace(/\/$/, '');
const getUserDetails = async (userId) => {
  const url = `${API_BASE_URL}/user/${userId}`.replace(/([^:]\/)\/+/g, "$1");
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error response:', errorText);
      throw new Error(`API request failed with status ${response.status}: ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getUserDetails:', error);
    throw error;
  }
};

const updateUserUseCase = async (userData) => {
  try {
    const url = `${API_BASE_URL}/user/use-case`;
    const accessToken = localStorage.getItem('accessToken');
    const result = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(userData)
    });
    if (!result.ok) {
      const errorText = await result.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to update use case: ${errorText}`);
    }
    const jsonResult = await result.json();
    return jsonResult;
  } catch (error) {
    console.error('Error in updateUserUseCase:', error);
    throw error;
  }
};

const OnboardingUseCase = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUseCase = useSelector(state => state.onboarding.useCase);
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');
  const [selectedUseCase, setSelectedUseCase] = useState(currentUseCase);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      if (userId) {
        try {
          const userData = await getUserDetails(userId);

          if (!userData || typeof userData !== 'object') {
            throw new Error('Invalid user data received');
          }
          setProfileData(userData);
          if (userData.useCase) {
            setSelectedUseCase(userData.useCase);
            dispatch(setUseCase(userData.useCase));
          }
          dispatch(setUserProfile(userData));
          localStorage.setItem('oldUserData', JSON.stringify(userData));
        } catch (error) {
          console.error('Error fetching user profile:', error);
          showToast(`Failed to load user profile: ${error.message}`, 'error');
        }
      } else {
        showToast('No user ID found. Please log in again.', 'error');
      }
      setLoading(false);
    };

    fetchUserProfile();
  }, [userId, dispatch]);

  useEffect(() => {
    setSelectedUseCase(currentUseCase);
  }, [currentUseCase]);

  const handleUseCaseChange = (value) => {
    setSelectedUseCase(value);
    const newUserData = { ...profileData, useCase: value };
    localStorage.setItem('newUserData', JSON.stringify(newUserData));
  };

  const handleSubmit = async () => {
    if (!selectedUseCase) {
      showToast('Please select a use case before saving.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const oldUserData = JSON.parse(localStorage.getItem('oldUserData') || '{}');
      const newUserData = { ...oldUserData, useCase: selectedUseCase };

      if (oldUserData.useCase === selectedUseCase) {
        showToast('No changes detected. Use case is up to date.', 'info');
        setLoading(false);
        dispatch(setCurrentStep(2));
        return;
      }

      // Prepare data for backend
      const userData = {
        email: profileData.email,
        useCase: selectedUseCase,
        userId: profileData.userId // Include userId in the update request
      };

      // Send data to backend
      const updatedProfile = await updateUserUseCase(userData);
      if (updatedProfile && updatedProfile.message === 'User use case updated successfully') {
        dispatch(setUseCase(selectedUseCase));
        
        // Update local profile data
        const updatedProfileData = { ...profileData, useCase: selectedUseCase };
        setProfileData(updatedProfileData);
        
        // Update old user data in local storage
        localStorage.setItem('oldUserData', JSON.stringify(updatedProfileData));
        showToast('Use case updated successfully', 'success');
        
        // Update the Redux store instead of navigating
        dispatch(setUserProfile(updatedProfileData));
        
        // Move to the next step (AI Talker Personality)
        dispatch(setCurrentStep(2));
      } else {
        console.error('Unexpected server response:', updatedProfile);
        throw new Error('Failed to update use case on the server');
      }
    } catch (error) {
      console.error('Error updating use case:', error);
      showToast(error.message || 'Failed to update use case', 'error');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box className="use-case-container" sx={{ padding: isMobile ? 2 : 3 }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom className="use-case-title">
        Select Use Case
      </Typography>
      <Typography variant="body1" gutterBottom className="use-case-description" sx={{ mb: isMobile ? 2 : 4 }}>
        Choose the primary use case for your AI Talker.
      </Typography>
      <Grid container spacing={isMobile ? 2 : 3} className="use-case-grid">
        {useCaseOptions.map((option, index) => (
          <Grid item xs={6} sm={6} md={4} key={option.value}>
            <Card 
              raised={selectedUseCase === option.value}
              className={`use-case-card ${selectedUseCase === option.value ? 'use-case-card-selected' : ''} fade-in`}
              style={{animationDelay: `${index * 0.1}s`}}
            >
              <CardActionArea 
                onClick={() => handleUseCaseChange(option.value)}
                className="use-case-card-content"
                sx={{ padding: isMobile ? 1 : 2 }}
              >
                <Box className={`use-case-icon ${selectedUseCase === option.value ? 'icon-selected' : ''}`}>
                  {React.cloneElement(option.icon, { fontSize: isMobile ? "medium" : "large" })}
                </Box>
                <CardContent>
                  <Typography variant={isMobile ? "body1" : "h6"} component="div" className="use-case-label">
                    {option.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      <OnboardingButtonDock
        onSave={handleSubmit}
        onBack={() => {
          dispatch(setCurrentStep(0)); 
        }}
        onNext={() => {
          if (selectedUseCase) {
            dispatch(setCurrentStep(2));
          } else {
            showToast('Please select a use case before proceeding.', 'warning');
          }
        }}
        title="save usecase"
        loading={loading}
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
      />
    </Box>
  );
};

export default OnboardingUseCase;
